// src/pages/account/creator/videos/VideoEditor.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Form,
  Button,
  Badge,
  Alert,
  Spinner,
} from "react-bootstrap";

import {
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../../../../shared/libs/firebaseConfig";
import { useAuth } from "../../../../shared/auth/AuthProvider";

// ปรับหมวดหมู่ตามที่คุณใช้จริง
const CATEGORY_OPTIONS = [
  "ทั้งหมด",
  "เนติบัณฑิต",
  "ทนายความ",
  "อัยการ",
  "ผู้พิพากษา",
  "ก.พ.",
];

const KIND_OPTIONS = [
  { value: "youtube", label: "YouTube (แนวนอน)" },
  { value: "appetizer", label: "Appetizer (YouTube แนวนอน)" },
  { value: "oral_exam_tips", label: "ติวสอบปากเปล่า (YouTube แนวนอน)" },
  { value: "podcast", label: "Podcast (YouTube แนวนอน)" },
  { value: "shorts", label: "YouTube Shorts (แนวตั้ง)" },
  { value: "shorts_prepare_for_exams", label: "YouTube Shorts (แนวตั้ง เตรียมสอบ)" },
  { value: "facebook", label: "Facebook" },
  { value: "vertical", label: "Vertical (Storage)" },
];

// helper: ดึง youtube id
function getYouTubeId(url = "") {
  try {
    const u = new URL(url);

    if (u.hostname.includes("youtu.be")) {
      return u.pathname.replace("/", "").split("?")[0];
    }

    if (u.searchParams.get("v")) {
      return u.searchParams.get("v");
    }

    if (u.pathname.includes("/shorts/")) {
      return u.pathname.split("/shorts/")[1]?.split("?")[0] || "";
    }

    if (u.pathname.includes("/embed/")) {
      return u.pathname.split("/embed/")[1]?.split("?")[0] || "";
    }

    return "";
  } catch {
    // เผื่อใส่เป็น id มาเลย
    return url?.trim?.() || "";
  }
}

function inferThumbUrl(kind, url) {
  const youtubeKinds = [
    "youtube",
    "appetizer",
    "oral_exam_tips",
    "podcast",
    "shorts",
    "shorts_prepare_for_exams",
  ];

  if (youtubeKinds.includes(kind)) {
    const id = getYouTubeId(url);
    if (!id) return "";
    return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
  }

  return "";
}

export default function VideoEditor() {
  const navigate = useNavigate();
  const { videoId } = useParams(); // /account/creator/videos/new หรือ /account/creator/videos/:videoId
  const isEdit = useMemo(() => !!videoId && videoId !== "new", [videoId]);

  const { user, userDoc } = useAuth();
  const roles = userDoc?.role;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [kind, setKind] = useState("youtube");
  const [url, setUrl] = useState("");
  const [thumbUrl, setThumbUrl] = useState("");
  const [isActive, setIsActive] = useState(true);

  // categories เป็น array
  const [categories, setCategories] = useState(["ก.พ."]);

  // vertical storage (ถ้าคุณจะใช้)
  const [fileUrl, setFileUrl] = useState("");
  const [storagePath, setStoragePath] = useState("");

  // load edit doc
  useEffect(() => {
    if (!user) return;

    (async () => {
      try {
        setErr("");
        setLoading(true);

        if (!isEdit) {
          setLoading(false);
          return;
        }

        const ref = doc(db, "videos", videoId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setErr("ไม่พบวิดีโอนี้");
          setLoading(false);
          return;
        }

        const data = snap.data();
        setTitle(data?.title || "");
        setDescription(data?.description || "");
        setKind(data?.kind || "youtube");
        setUrl(data?.url || "");
        setThumbUrl(data?.thumbUrl || "");
        setIsActive(data?.isActive ?? true);
        setCategories(Array.isArray(data?.categories) ? data.categories : []);
        setFileUrl(data?.fileUrl || "");
        setStoragePath(data?.storagePath || "");

        setLoading(false);
      } catch (e) {
        console.error(e);
        setErr(e?.message || "โหลดข้อมูลไม่สำเร็จ");
        setLoading(false);
      }
    })();
  }, [user, isEdit, videoId]);

  // auto thumb for youtube/podcast/shorts
  useEffect(() => {
    if (thumbUrl) return; // ถ้ากรอกเองแล้วไม่ทับ
    const auto = inferThumbUrl(kind, url);
    if (auto) setThumbUrl(auto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, url]);

 const layout = useMemo(() => {
    if (kind === "vertical" || kind === "shorts" || kind === "shorts_prepare_for_exams") {
      return "vertical";
    }
    return "horizontal";
  }, [kind]);

  const toggleCategory = (c) => {
    if (c === "ทั้งหมด") return; // ไม่ต้องเก็บ "ทั้งหมด" ใน doc (ใช้ที่ UI filter)
    setCategories((prev) => {
      const set = new Set(prev || []);
      if (set.has(c)) set.delete(c);
      else set.add(c);
      return Array.from(set);
    });
  };

  const canEdit = useMemo(() => {
    // แนะนำให้ใช้ rules คุมอีกชั้น แต่ใน UI กันไว้ก่อน
    const r = Array.isArray(roles) ? roles : [roles].filter(Boolean);
    const set = new Set(r.map((x) => String(x).toLowerCase()));
    return set.has("admin") || set.has("creator");
  }, [roles]);

  const buildPayload = () => {
    const createdBy = {
      uid: user?.uid || "",
      role: (Array.isArray(roles) ? roles[0] : roles) || "creator",
      name: userDoc?.displayName || user?.displayName || "",
    };

    return {
      title: title.trim(),
      description: description.trim(),
      categories: (categories || []).filter(Boolean),
      kind,
      url: kind !== "vertical" ? url.trim() : "",
      thumbUrl: thumbUrl.trim(),
      isActive: !!isActive,

      layout,
      fileUrl: kind === "vertical" ? fileUrl.trim() : "",
      storagePath: kind === "vertical" ? storagePath.trim() : "",

      ownerUid: user?.uid || "",
      createdBy,

      updatedAt: serverTimestamp(),
    };
  };

  const onSave = async (e) => {
    e?.preventDefault?.();
    if (!canEdit) return;

    setErr("");
    if (!title.trim()) return setErr("กรุณากรอกชื่อวิดีโอ");
    if (!description.trim()) return setErr("กรุณากรอกคำอธิบายโดยย่อ");
    if (!categories?.length) return setErr("กรุณาเลือกหมวดหมู่อย่างน้อย 1 หมวด");

    if (kind !== "vertical" && !url.trim()) {
      return setErr("กรุณากรอกลิงก์วิดีโอ");
    }

    if (kind === "vertical" && !fileUrl.trim() && !storagePath.trim()) {
      return setErr("Vertical: กรุณากรอก fileUrl หรือ storagePath อย่างน้อย 1 ค่า");
    }

    try {
      setSaving(true);

      if (!isEdit) {
        const payload = {
          ...buildPayload(),
          createdAt: serverTimestamp(),
          viewCounts: 0,
        };
        const ref = await addDoc(collection(db, "videos"), payload);
        setSaving(false);
        navigate(`/account/creator/videos/${ref.id}`, { replace: true });
        return;
      }

      const ref = doc(db, "videos", videoId);
      await updateDoc(ref, buildPayload());
      setSaving(false);
    } catch (e2) {
      console.error(e2);
      setErr(e2?.message || "บันทึกไม่สำเร็จ");
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!isEdit) return;
    const ok = window.confirm("ยืนยันลบวิดีโอนี้?");
    if (!ok) return;

    try {
      setSaving(true);
      await deleteDoc(doc(db, "videos", videoId));
      setSaving(false);
      navigate("/account/creator/videos", { replace: true });
    } catch (e) {
      console.error(e);
      setErr(e?.message || "ลบไม่สำเร็จ");
      setSaving(false);
    }
  };

  const Preview = () => {
    if (loading) {
      return (
        <div className="py-5 text-center text-muted">
          <Spinner animation="border" size="sm" /> กำลังโหลด…
        </div>
      );
    }

    if (
      kind === "youtube" ||
      kind === "appetizer" ||
      kind === "oral_exam_tips" ||
      kind === "podcast"
    ) {
      const id = getYouTubeId(url);
      if (!id) return <div className="text-muted">ใส่ลิงก์ YouTube เพื่อพรีวิว</div>;

      return (
        <div className="ratio ratio-16x9">
          <iframe
            src={`https://www.youtube.com/embed/${id}`}
            title="YouTube preview"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    }

    if (kind === "shorts" || kind === "shorts_prepare_for_exams") {
      const id = getYouTubeId(url);
      if (!id) return <div className="text-muted">ใส่ลิงก์ YouTube Shorts เพื่อพรีวิว</div>;

      return (
        <div
          style={{
            width: "100%",
            maxWidth: 420,
            margin: "0 auto",
          }}
        >
          <div className="ratio" style={{ "--bs-aspect-ratio": "177%" }}>
            <iframe
              src={`https://www.youtube.com/embed/${id}`}
              title="YouTube Shorts preview"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ border: 0, borderRadius: 12 }}
            />
          </div>
        </div>
      );
    }

    if (kind === "facebook") {
      if (!url?.trim()) return <div className="text-muted">ใส่ลิงก์ Facebook เพื่อพรีวิว</div>;

      const plugin = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(
        url.trim()
      )}&show_text=false&width=560`;

      return (
        <div className="ratio ratio-16x9">
          <iframe
            src={plugin}
            title="Facebook preview"
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    }

    // vertical/storage
    if (kind === "vertical") {
      const src = fileUrl?.trim() || "";
      if (!src) {
        return (
          <div className="text-muted">
            ใส่ fileUrl (แนะนำ) หรือ storagePath แล้วค่อยทำส่วนแปลงเป็น URL ภายหลัง
          </div>
        );
      }

      return (
        <div className="ratio" style={{ "--bs-aspect-ratio": "177%" }}>
          <video
            src={src}
            controls
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </div>
      );
    }

    return <div className="text-muted">ยังไม่มีพรีวิว</div>;
  };

  if (!user) return null;
  if (!canEdit) return <Alert variant="warning">หน้านี้สำหรับ Creator/Admin เท่านั้น</Alert>;

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h4 className="mb-0">{isEdit ? "แก้ไขวิดีโอ" : "เพิ่มวิดีโอ"}</h4>
          <div className="text-muted small">
            Collection: <code>videos</code>
          </div>
        </div>

        <div className="d-flex gap-2">
          {isEdit && (
            <Button variant="outline-danger" onClick={onDelete} disabled={saving}>
              ลบ
            </Button>
          )}
          <Button variant="outline-secondary" onClick={() => navigate(-1)} disabled={saving}>
            กลับ
          </Button>
          <Button variant="primary" onClick={onSave} disabled={saving}>
            {saving ? "กำลังบันทึก..." : "บันทึก"}
          </Button>
        </div>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      <Row className="g-3">
        <Col lg={6}>
          <Card className="border">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="fw-semibold">พรีวิว</div>
                <Badge bg={isActive ? "success" : "secondary"}>
                  {isActive ? "Active" : "Hidden"}
                </Badge>
              </div>

              <Preview />

              {!!thumbUrl && (
                <div className="mt-3">
                  <div className="text-muted small mb-1">Thumbnail</div>
                  <img
                    src={thumbUrl}
                    alt="thumb"
                    style={{
                      width: "100%",
                      borderRadius: 10,
                      border: "1px solid rgba(0,0,0,.1)",
                    }}
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="border">
            <Card.Body>
              <Form onSubmit={onSave}>
                <Form.Group className="mb-3">
                  <Form.Label>ชื่อวิดีโอ</Form.Label>
                  <Form.Control
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="เช่น สรุปกฎหมายปกครองใน 10 นาที"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>คำอธิบายโดยย่อ</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="คำอธิบายโดยย่อจากหัวข้อวิดีโอ"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>ประเภท (kind)</Form.Label>
                  <Form.Select value={kind} onChange={(e) => setKind(e.target.value)}>
                    {KIND_OPTIONS.map((k) => (
                      <option key={k.value} value={k.value}>
                        {k.label}
                      </option>
                    ))}
                  </Form.Select>
                  <div className="text-muted small mt-1">
                    ระบบจะ set <code>layout</code> เป็น <code>{layout}</code> ให้อัตโนมัติ
                  </div>
                </Form.Group>

                {kind !== "vertical" ? (
                  <Form.Group className="mb-3">
                    <Form.Label>ลิงก์วิดีโอ (url)</Form.Label>
                    <Form.Control
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder={
                        kind === "facebook"
                          ? "https://www.facebook.com/.../videos/..."
                          : kind === "shorts" || kind === "shorts_prepare_for_exams"
                          ? "https://www.youtube.com/shorts/xxxxxxxxxxx"
                          : "https://www.youtube.com/watch?v=..."
                      }
                    />
                    {(["youtube", "appetizer", "oral_exam_tips", "podcast", "shorts", "shorts_prepare_for_exams"].includes(kind)) && (
                      <div className="text-muted small mt-1">
                        รองรับทั้งลิงก์แบบ watch, youtu.be และ shorts
                      </div>
                    )}
                  </Form.Group>
                ) : (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Vertical: fileUrl</Form.Label>
                      <Form.Control
                        value={fileUrl}
                        onChange={(e) => setFileUrl(e.target.value)}
                        placeholder="https://firebasestorage.googleapis.com/..."
                      />
                      <div className="text-muted small mt-1">
                        ถ้าตอนนี้ยังไม่ทำ upload ในหน้านี้ ให้ใช้ fileUrl ไปก่อน
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Vertical: storagePath (optional)</Form.Label>
                      <Form.Control
                        value={storagePath}
                        onChange={(e) => setStoragePath(e.target.value)}
                        placeholder="videos/vertical/abc.mp4"
                      />
                    </Form.Group>
                  </>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>Thumbnail URL (thumbUrl)</Form.Label>
                  <Form.Control
                    value={thumbUrl}
                    onChange={(e) => setThumbUrl(e.target.value)}
                    placeholder="ปล่อยว่างได้ (YouTube / Shorts จะเดาให้)"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>หมวดหมู่ (categories)</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    {CATEGORY_OPTIONS.filter((c) => c !== "ทั้งหมด").map((c) => {
                      const active = categories?.includes(c);
                      return (
                        <Badge
                          key={c}
                          bg={active ? "primary" : "light"}
                          text={active ? "white" : "dark"}
                          role="button"
                          style={{
                            cursor: "pointer",
                            border: "1px solid rgba(0,0,0,.12)",
                          }}
                          onClick={() => toggleCategory(c)}
                        >
                          {c}
                        </Badge>
                      );
                    })}
                  </div>
                  <div className="text-muted small mt-2">
                    เลือกได้หลายหมวด (เก็บเป็น array)
                  </div>
                </Form.Group>

                <Form.Check
                  className="mb-3"
                  type="switch"
                  id="isActive"
                  label="แสดงผล (isActive)"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />

                <div className="d-grid gap-2">
                  <Button type="submit" variant="primary" disabled={saving}>
                    {saving ? "กำลังบันทึก..." : "บันทึก"}
                  </Button>
                </div>

                <hr className="my-4" />
                <div className="text-muted small">
                  จะบันทึกฟิลด์: <code>ownerUid</code>, <code>createdBy</code>,{" "}
                  <code>createdAt</code>/<code>updatedAt</code> อัตโนมัติ
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}